import { AsyncPipe, DatePipe, NgFor, NgIf } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PrimeTemplate } from 'primeng/api';
import { DatePickerModule } from 'primeng/datepicker';
import { DrawerModule } from 'primeng/drawer';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { SkeletonModule } from 'primeng/skeleton';
import { Table, TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { SelectModule } from 'primeng/select';
import { Subscription } from 'rxjs';
import { ExcelService } from 'src/app/core/services/excel.service';
import { ExcelRequestColumn } from 'src/app/models/ExcelRequestColumn';
import { FilterResponse } from 'src/app/models/FilterResponse';
import { PersonQuery } from 'src/app/models/OpenData.ODDetailResponse';
import { QrCodeDialogComponent } from 'src/app/shared/components/qr-code-dialog/qr-code-dialog.component';
import { LoadingComponent } from 'src/app/utils/loading/loading.component';
import { UtilsService } from 'src/app/utils/utils.service';
import { ColumnFilterDirective } from '../../../shared/directives/p-columnFilter.directive';
import { TableDirective } from '../../../shared/directives/p-table.directive';
import { InnovationRequestDetailService } from '../innovation-request-detail/innovation-request-detail.service';
import { NewInnovationRequestComponent } from '../new-innovation-request/new-innovation-request.component';
import { NewInnovationRequestService } from '../new-innovation-request/new-innovation-request.service';
import { InnovationRequestService } from './innovation-request.service';
import { InnovationRequestsFilterService } from './innovation-requests-filter.service';
import { InnovationRequestsActiveFiltersComponent } from './standard-requests-active-filters/innovation-requests-active-filters.component';

@Component({
    selector: 'app-innovation-requests',
    templateUrl: './innovation-requests.component.html',
    standalone: true,
    imports: [
        NgIf,
        DrawerModule,
        PrimeTemplate,
        NewInnovationRequestComponent,
        FormsModule,
        ReactiveFormsModule,
        TableModule,
        TableDirective,
        TooltipModule,
        InnovationRequestsActiveFiltersComponent,
        IconFieldModule,
        InputIconModule,
        InputTextModule,
        ColumnFilterDirective,
        SelectModule,
        DatePickerModule,
        NgFor,
        SkeletonModule,
        TranslateModule,
        AsyncPipe,
        DatePipe,
        LoadingComponent,
        QrCodeDialogComponent
    ]
})
export class InnovationRequestsComponent implements OnInit, OnDestroy {

    isLoading = false;
    filterResponse: FilterResponse;
    sidebarVisible = false;
    private filterHandlerSubscription!: Subscription;
	@ViewChild('table') table: Table;

	constructor(
        private readonly router: Router,
		readonly innovationRequestsFilterService: InnovationRequestsFilterService,
		readonly innovationRequestService: InnovationRequestService,
		readonly innovationRequestDetailService: InnovationRequestDetailService,
		readonly newInnovationRequestService: NewInnovationRequestService,
		readonly utilsService: UtilsService,
        private readonly excelService: ExcelService,
        private readonly translate: TranslateService,
        private readonly datePipe: DatePipe
	) {
	    this.filterHandlerSubscription = this.innovationRequestsFilterService.filterHandler().subscribe((filteredResponse: FilterResponse) => {
	        this.filterResponse = filteredResponse;
	    });
	}

	async ngOnInit(): Promise<void> {
	    await this.innovationRequestsFilterService.search(true);
	}

	ngOnDestroy(): void {
	    if (this.filterHandlerSubscription) {
	        this.filterHandlerSubscription.unsubscribe();
	    }
	}

	goToDetail(queryId: string): void {
	    this.isLoading = true;
	    this.router.navigate([`/innovation/request-detail/${queryId}`])
	        .catch(error => { console.error(error); })
	        .finally(() => this.isLoading = false);
	}

	async exportToExcel(): Promise<void> {
	    const cols: ExcelRequestColumn[] = [
	        {
	            key: 'queryId',
	            i18n: 'MY_REQUESTS.IDENTIFIER',
	            width:36
	        },
	        {
	            key: 'createdDateTimeUTC',
	            i18n: 'MY_REQUESTS.DATE',
	            width: 22,
	            value: (r) => this.datePipe.transform(r.createdDateTime, 'dd/MM/yyyy HH:mm:ss', 'Europe/Madrid')
	        },
	        {
	            key: 'queryType',
	            i18n: 'MY_REQUESTS.TYPE',
	            width: 24,
	            value: (r) => this.getQueryType(r)
	        },
	        {
	            key: 'personId',
	            i18n: 'MY_REQUESTS.TIN',
	            width: 12,
	            value: (r) => this.getTin(r)
	        },
	        {
	            key: 'name',
	            i18n: 'MY_REQUESTS.NAME_OR_LEGAL_NAME',
	            width: 34,
	            value: (r) => this.getNameOrLegalName(r)
	        },
	        {
	            key: 'phoneNumber',
	            i18n: 'MY_REQUESTS.PHONE',
	            width: 12
	        },
	        {
	            key: 'statusi18n',
	            i18n: 'MY_REQUESTS.STATUS',
	            width: 14,
	            value: (r) => this.translate.instant(`STATUS.${r.status}`)
	        },
	        {
	            key: 'substatusi18n',
	            i18n: 'MY_REQUESTS.SUBSTATUS',
	            width: 42,
	            value: (r) => this.translate.instant(`SUBSTATUS.${r.substatus}`)
	        },
	        {
	            key: 'requestReference',
	            i18n: 'MY_REQUESTS.USER',
	            width: 24,
	            value: (r) => {
	                const reqRef: string = r.requestReference ?? '';
	                if (!reqRef) return '';
	                const start = reqRef.includes('SBD') ? 8 : 4;
	                const length = 7;
	                if (reqRef.length < start + length) return reqRef;
	                return reqRef.substring(start, start + length);
	            }
	        }
	    ];

	    await this.excelService.exportList(this.filterResponse?.requestList, cols);
	}

	newRequest(): void {
	    this.newInnovationRequestService.myForm.reset();
	    this.sidebarVisible = true;
	}

	globalSearch($event: Event): void {
	    const input = $event.target as HTMLInputElement;
	    this.innovationRequestsFilterService.globalSearchSubject.next({ inputValue: input.value, inputFallBackReference: input });
	}

	checkFilterFieldHasValue(field: string): boolean {
	    return this.innovationRequestsFilterService.form.get(field).value != null && this.innovationRequestsFilterService.form.get(field).value != '';
	}

	showIfTableIsLoadingAndEmpty(): boolean {
	    return (this.filterResponse?.requestList?.length == 0 || this.filterResponse == null) && this.innovationRequestsFilterService.isLoading;
	}

	onSortChange(event: any): void {
	    const field = event.field;
	    const order = event.order;
	    this.innovationRequestsFilterService.setSortField(field, order);
	}

	async sync(): Promise<void> {
	    this.innovationRequestsFilterService.resetSortField();
	    if (this.table) {
	        this.table.reset();	// refresh the table to show the new data
	    }
	    await this.innovationRequestsFilterService.search(true);
	}

	protected getTin(pq: PersonQuery): string {
	    return pq.belenderCircuit === 6 ? pq.companyId : pq.personId;
	}

	protected getNameOrLegalName(pq: PersonQuery): string {
	    return pq.belenderCircuit === 6 ? pq.companyName : this.utilsService.getFullName(pq);
	}

	protected getQueryType(pq: PersonQuery): string {
	    if (pq.belenderCircuit === 6) {
	        return this.translate.instant('FILTER.QUERY_TYPES.LEGAL_PERSON');
	    } else {
	        const personType = pq.personType === '00' ? 'GUARANTOR' : pq.personType;
	        return this.translate.instant(`PERSON_TYPES.${personType}`);
	    }
	}
}
